import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FileDownloadIcon   from "@mui/icons-material/FileDownload";
import ActionButton from "../../component/common/ActionButton";
import LockOutlinedIcon   from "@mui/icons-material/LockOutlined";
import { runApiAction } from "../../util/apiFeedback";
import { useMyPermissions } from "../../util/useMyPermissions";

import { ds } from "../../styles/designSystem";
import { financeApi } from "./api/financeApi";
import { downloadExcel } from "../../util/downloadExcel";
import { FINANCE_NAV, tabUrl } from "../../config/moduleNav";

import DashboardTab          from "./tabs/status/DashboardTab";
import CashFlowTab           from "./tabs/status/CashFlowTab";
import DailyCashTab          from "./tabs/status/DailyCashTab";
import TransactionTab        from "./tabs/status/TransactionTab";
import ProjectPnLTab         from "./tabs/status/ProjectPnLTab";
import ExpensePlanReportTab  from "./tabs/status/ExpensePlanReportTab";
import FixedCostCalendarTab  from "./tabs/status/FixedCostCalendarTab";
import CashCalendarTab       from "./tabs/status/CashCalendarTab";
import ExpenseRequestTab     from "./tabs/status/ExpenseRequestTab";
import ExpenseRequestByProjectTab from "./tabs/status/ExpenseRequestByProjectTab";
import ReceivableTab         from "./tabs/management/ReceivableTab";
import ApprovalLinkTab       from "./tabs/management/ApprovalLinkTab";
import BudgetTab             from "./tabs/management/BudgetTab";
import VendorTab             from "./tabs/management/VendorTab";
import FinanceDeptSummaryTab from "./tabs/status/FinanceDeptSummaryTab";

import FinanceLinkProvider from "./components/FinanceLinkProvider";

function NoPermission({ tabLabel }) {
    return (
        <Box sx={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", py: 10, color: "#94a3b8",
        }}>
            <LockOutlinedIcon sx={{ fontSize: 44, mb: 1.5, opacity: 0.4 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>접근 권한이 없습니다</Typography>
            <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                [{tabLabel}] 탭을 열람할 권한이 없습니다. 관리자에게 문의하세요.
            </Typography>
        </Box>
    );
}

export default function FinancePage() {
    const { hasPerm } = useMyPermissions();
    const location = useLocation();
    const history  = useHistory();
    const [departments, setDepartments]         = useState([]);
    const [projectChannels, setProjectChannels] = useState([]);
    const [exporting, setExporting]             = useState(false);

    useEffect(() => {
        financeApi.getDepartments().then(data => setDepartments(data || [])).catch(() => {});
        financeApi.getProjectChannels().then(data => setProjectChannels(data || [])).catch(() => {});
    }, []);

    // 대시보드/부서별 현황에서 팀 클릭 시 ?deptId=&ym= 으로 넘어옴 → 목록을 그 팀·그 달로 프리필터
    const sp          = new URLSearchParams(location.search);
    const deptIdParam = sp.get("deptId");
    const ymParam     = sp.get("ym");

    /** 탭 key → 실제 컴포넌트. 이름(label)은 config(FINANCE_NAV)에만, 여기는 key로만 매핑. */
    const renderTab = (key) => {
        switch (key) {
            case "txlist":      return <TransactionTab departments={departments} projectChannels={projectChannels} initialDeptId={deptIdParam} initialMonth={ymParam} />;
            case "receivable":  return <ReceivableTab projectChannels={projectChannels} />;
            case "vendor":      return <VendorTab />;
            case "budget":      return <BudgetTab departments={departments} />;
            case "dashboard":   return <DashboardTab />;
            case "cashflow":    return <CashFlowTab departments={departments} projectChannels={projectChannels} mode="view" />;
            // 두 탭은 같은 컴포넌트를 쓰므로 key 를 달리해 인스턴스를 분리한다.
            // (key 가 없으면 React 가 같은 위치·같은 타입으로 보고 인스턴스를 재사용 → 탭을 바꿔도
            //  상태 필터·검색어 등 이전 탭 state 가 그대로 남아 긴급 보고서에 '예정'이 섞여 보였다.)
            case "expensereport": return <ExpensePlanReportTab key="expensereport" departments={departments} />;
            // 긴급 보고서는 URGENT 한 상태만 보는 전용 화면 — lockStatus 를 빼면 기본(예정·우선지급) 필터가
            // 적용돼 '긴급만 보여야 하는' 화면에 예정 건이 그대로 섞여 나온다.
            case "urgentexpense": return <ExpensePlanReportTab key="urgentexpense" departments={departments} lockStatus="URGENT" />;
            case "fixedcalendar": return <FixedCostCalendarTab />;
            case "cashcalendar":  return <CashCalendarTab />;
            case "deptsummary": return <FinanceDeptSummaryTab />;
            case "project":     return <ProjectPnLTab departments={departments} projectChannels={projectChannels} />;
            case "dailycash":   return <DailyCashTab />;
            case "approvallink":return <ApprovalLinkTab />;
            case "expenserequest": return <ExpenseRequestTab departments={departments} />;
            // 같은 결재를 프로젝트 기준으로 묶는 화면. 사이드바에 프로젝트를 펼치지 않고
            // 화면 안 드롭다운으로 고른다(프로젝트는 계속 늘어난다 — moduleNav.js 참고).
            case "expenserequestproject": return <ExpenseRequestByProjectTab projectChannels={projectChannels} />;
            default:            return null;
        }
    };

    // ── 현재 섹션 (?section=) ─ 기본 view ──
    const sectionKey = useMemo(() => {
        const s = new URLSearchParams(location.search).get("section");
        return FINANCE_NAV.sections.some(x => x.key === s) ? s : "view";
    }, [location.search]);
    const section = FINANCE_NAV.sections.find(s => s.key === sectionKey);

    // 권한 있는 탭만
    const visibleTabs = useMemo(
        () => section.tabs.filter(t => hasPerm(t.perm)),
        [section, hasPerm]
    );

    // ── 활성 탭은 URL(?tab=)에서 파생 ─ 사이드바 트리와 양방향 동기화 ──
    const tabParam = new URLSearchParams(location.search).get("tab");
    const activeIdx = useMemo(() => {
        const i = visibleTabs.findIndex(t => t.label === tabParam);
        return i >= 0 ? i : 0;
    }, [visibleTabs, tabParam]);
    const activeLabel = visibleTabs[activeIdx]?.label;

    // URL 에 항상 현재 섹션+탭이 담기도록 보정 → 사이드바가 활성 탭을 정확히 하이라이트한다.
    useEffect(() => {
        if (activeLabel && tabParam !== activeLabel) {
            history.replace(tabUrl(FINANCE_NAV, sectionKey, activeLabel));
        }
    }, [sectionKey, tabParam, activeLabel, history]);

    const handleTabChange = (_, idx) => {
        const label = visibleTabs[idx]?.label;
        if (label) history.push(tabUrl(FINANCE_NAV, sectionKey, label));
    };

    const handleExport = () => runApiAction({
        request: () => downloadExcel("finance"),
        successMsg: "엑셀 파일이 다운로드되었습니다.",
        errorMsg: (msg) => "엑셀 내보내기 실패: " + (msg || "알 수 없는 오류"),
        setLoading: setExporting,
    });

    if (visibleTabs.length === 0) {
        return (
            <Box sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 3 }}>
                <NoPermission tabLabel="재무 전체" />
            </Box>
        );
    }

    const safeIdx = Math.min(activeIdx, visibleTabs.length - 1);

    return (
        <Box sx={{ minHeight: "100%", bgcolor: "#fff" }}>
        <Box sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Typography sx={ds.typography.pageTitle}>재무</Typography>
                <Box sx={{ flex: 1 }} />


                {/*<ActionButton*/} {/*//TODO 엑셀 내보내기는 추후 추에*/}
                {/*    variant="secondary"*/}
                {/*    startIcon={<FileDownloadIcon sx={{ fontSize: 15 }} />}*/}
                {/*    onClick={handleExport}*/}
                {/*    loading={exporting}*/}
                {/*    label={exporting ? "준비 중..." : "엑셀 내보내기"}*/}
                {/*    sx={{ fontSize: 12 }}*/}
                {/*/>*/}
            </Box>
            <Typography sx={{ ...ds.typography.pageSubtitle, mb: 2.5 }}>
                {section.subtitle}
            </Typography>

            <Box sx={{ borderBottom: "1px solid #e2e8f0", mb: 2.5 }}>
                <Tabs
                    value={safeIdx}
                    onChange={handleTabChange}
                    sx={ds.components.tabs.root}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {visibleTabs.map((t) => <Tab key={t.key} label={t.label} />)}
                </Tabs>
            </Box>

            <FinanceLinkProvider>
                {renderTab(visibleTabs[safeIdx]?.key)}
            </FinanceLinkProvider>
        </Box>
        </Box>
    );
}
